// Master list of crypto/finance basic terms to detect in report text.
// Key = canonical display term, value = array of surface forms to match (case-insensitive).
// Sorted roughly by category for maintainability.

export const CRYPTO_TERMS = {
  // ── Tokenomics ────────────────────────────────────────────────────
  'FDV':              ['FDV', 'Fully Diluted Valuation', 'Fully Diluted Value'],
  'Market Cap':       ['Market Cap', 'Market Capitalization', 'Mkt Cap'],
  'Tokenomics':       ['Tokenomics', 'Token Economics'],
  'Vesting':          ['Vesting', 'Vesting Schedule', 'Vesting Period'],
  'Cliff':            ['Cliff', 'Cliff Period', 'Lock-up Cliff'],
  'TGE':              ['TGE', 'Token Generation Event'],
  'Circulating Supply':['Circulating Supply', 'Circulating'],
  'Total Supply':     ['Total Supply'],
  'Inflation':        ['Inflation', 'Inflationary'],
  'Deflation':        ['Deflation', 'Deflationary'],
  'Token Burn':       ['Token Burn', 'Burn Mechanism', 'Buy-back and Burn', 'Buyback'],
  'Airdrop':          ['Airdrop', 'Airdrop Snapshot', 'Airdrop Claim'],
  'Unlock':           ['Token Unlock', 'Unlock Schedule', 'Unlock Event'],
  'Sell Pressure':    ['Sell Pressure', 'Selling Pressure'],
  'Staking':          ['Staking', 'Staking Rewards', 'Staking APY'],
  'Yield':            ['Yield', 'Yield Farming'],
  'Governance':       ['Governance', 'Governance Voting', 'On-chain Governance'],

  // ── DeFi ──────────────────────────────────────────────────────────
  'TVL':              ['TVL', 'Total Value Locked'],
  'DEX':              ['DEX', 'Decentralized Exchange'],
  'CEX':              ['CEX', 'Centralized Exchange'],
  'AMM':              ['AMM', 'Automated Market Maker'],
  'Liquidity Pool':   ['Liquidity Pool', 'LP', 'Liquidity Providers'],
  'Liquidity':        ['Liquidity'],
  'Slippage':         ['Slippage'],
  'Flash Loan':       ['Flash Loan', 'Flash Loans'],
  'Lending Protocol': ['Lending Protocol', 'Lending Platform'],
  'Collateral':       ['Collateral', 'Over-collateralized'],
  'Liquidation':      ['Liquidation', 'Liquidate'],
  'RWA':              ['RWA', 'Real-World Asset', 'Real World Assets'],
  'CDP':              ['CDP', 'Collateralized Debt Position'],

  // ── Layer / Architecture ──────────────────────────────────────────
  'L1':               ['L1', 'Layer 1', 'Layer-1'],
  'L2':               ['L2', 'Layer 2', 'Layer-2'],
  'Rollup':           ['Rollup', 'Roll-up'],
  'ZK-Rollup':        ['ZK-Rollup', 'ZK Rollup', 'ZK-rollup'],
  'Optimistic Rollup':['Optimistic Rollup'],
  'ZK Proof':         ['ZK Proof', 'Zero-Knowledge Proof', 'ZK-proof', 'ZK proofs', 'STARK', 'SNARK', 'zk-STARK', 'zk-SNARK'],
  'EVM':              ['EVM', 'EVM-compatible', 'Ethereum Virtual Machine'],
  'Mainnet':          ['Mainnet', 'Main Network'],
  'Testnet':          ['Testnet', 'Test Network'],
  'Sharding':         ['Sharding', 'Shard'],
  'Cross-chain':      ['Cross-chain', 'Cross chain', 'Multi-chain', 'Multichain', 'Interoperability'],
  'Bridge':           ['Bridge', 'Cross-chain Bridge'],
  'Sequencer':        ['Sequencer'],
  'Data Availability':['Data Availability', 'DA Layer'],

  // ── Market / Trading ─────────────────────────────────────────────
  'ATH':              ['ATH', 'All-Time High'],
  'ATL':              ['ATL', 'All-Time Low'],
  'Bull Case':        ['Bull Case', 'Bull market', 'Bullish'],
  'Bear Case':        ['Bear Case', 'Bear market', 'Bearish'],
  'Volatility':       ['Volatility'],
  'Liquidity Mining': ['Liquidity Mining'],
  'Market Maker':     ['Market Maker'],
  'Order Book':       ['Order Book'],
  'Spot Trading':     ['Spot Trading', 'Spot Market'],
  'Perpetuals':       ['Perpetuals', 'Perps', 'Perpetual Futures'],
  'Funding Rate':     ['Funding Rate'],
  'Open Interest':    ['Open Interest', 'OI'],

  // ── Blockchain fundamentals ───────────────────────────────────────
  'Consensus':        ['Consensus', 'Consensus Mechanism'],
  'Proof of Stake':   ['Proof of Stake', 'PoS'],
  'Proof of Work':    ['Proof of Work', 'PoW'],
  'Validator':        ['Validator', 'Validators'],
  'Node':             ['Node', 'Full Node'],
  'Smart Contract':   ['Smart Contract', 'Smart Contracts'],
  'Gas':              ['Gas Fee', 'Gas Fees', 'Gas Cost'],
  'Wallet':           ['Wallet', 'Non-custodial Wallet', 'Custodial Wallet'],
  'Private Key':      ['Private Key'],
  'Seed Phrase':      ['Seed Phrase', 'Recovery Phrase', 'Mnemonic'],
  'DAO':              ['DAO', 'Decentralized Autonomous Organization'],
  'NFT':              ['NFT', 'Non-Fungible Token'],
  'Oracle':           ['Oracle', 'Price Oracle', 'Chainlink Oracle'],

  // ── VC / Funding ─────────────────────────────────────────────────
  'VC':               ['VC', 'Venture Capital', 'Venture Capitalist'],
  'Seed Round':       ['Seed Round', 'Seed Funding'],
  'Series A':         ['Series A'],
  'Strategic Round':  ['Strategic Round', 'Strategic Investment'],
  'Token Sale':       ['Token Sale', 'Public Sale', 'Private Sale'],
  'IDO':              ['IDO', 'Initial DEX Offering'],
  'ICO':              ['ICO', 'Initial Coin Offering'],
  'IEO':              ['IEO', 'Initial Exchange Offering'],
  'Valuation':        ['Valuation', 'Post-money Valuation'],

  // ── Security ─────────────────────────────────────────────────────
  'Audit':            ['Audit', 'Security Audit', 'Smart Contract Audit'],
  'Rug Pull':         ['Rug Pull', 'Rug'],
  'Exploit':          ['Exploit', 'Vulnerability'],
  'Hack':             ['Hack', 'Hacked', 'Protocol Hack'],
  'MEV':              ['MEV', 'Maximal Extractable Value'],
};

// Build a flat array of { surfaceForm, canonical } sorted longest-first
// to ensure longer phrases match before shorter ones (e.g. "ZK-Rollup" before "ZK")
export const TERM_MAP = Object.entries(CRYPTO_TERMS).flatMap(([canonical, forms]) =>
  forms.map(sf => ({ surfaceForm: sf, canonical }))
).sort((a, b) => b.surfaceForm.length - a.surfaceForm.length);
